import React, { useEffect, useState } from 'react';
import { whatsappApi, formatApiError } from '../services/api';
import WhatsAppChatDrawer from '../components/WhatsAppChatDrawer';
import { toast } from 'sonner';
import { WhatsappLogo, ChatCircleDots } from '@phosphor-icons/react';

const rel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'gerade eben';
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std`;
  return d.toLocaleDateString('de-DE');
};

const Messages = () => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const thr = await whatsappApi.threads();
      setThreads(thr.data?.threads || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openChat = (thread) => {
    if (!thread.customer_id || !thread.customer) return;
    setActive(thread.customer);
  };

  return (
    <div className="p-6 space-y-6" data-testid="messages-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#111418] flex items-center gap-2">
            <WhatsappLogo weight="fill" size={28} className="text-[#25D366]" />
            Nachrichten
          </h1>
          <p className="text-[#525866] mt-1">
            WhatsApp Business — Chat mit Kunden direkt aus der App
          </p>
        </div>
      </div>

      {loading ? (
        <div className="card-swiss p-12 text-center text-[#525866]">Lade …</div>
      ) : threads.length === 0 ? (
        <div className="card-swiss p-12 text-center text-[#525866]">
          <ChatCircleDots weight="bold" size={40} className="mx-auto text-[#8A94A6] mb-3" />
          <p className="font-semibold">Noch keine WhatsApp-Konversationen</p>
          <p className="text-sm mt-1">Öffne einen Kunden und sende die erste Nachricht.</p>
        </div>
      ) : (
        <div className="card-swiss overflow-hidden">
          <ul className="divide-y divide-[#E2E4E9]">
            {threads.map((t) => {
              const cust = t.customer;
              const last = t.last_message;
              const isInbound = last?.direction === 'inbound';
              const unread = t.unread_count > 0;
              return (
                <li key={t.customer_id || Math.random()}>
                  <button
                    type="button"
                    onClick={() => openChat(t)}
                    disabled={!cust}
                    className={`w-full text-left p-4 hover:bg-[#F8F9FB] transition flex items-center gap-3 ${unread ? 'bg-emerald-50/40' : ''}`}
                    data-testid={`wa-thread-${t.customer_id || 'unknown'}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#25D366] text-white flex items-center justify-center font-semibold">
                      {cust ? `${(cust.first_name || '?')[0]}${(cust.last_name || '')[0] || ''}` : '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-[#111418] truncate">
                          {cust ? `${cust.first_name} ${cust.last_name}` : (last?.from || 'Unbekannt')}
                        </p>
                        <span className="text-[11px] text-[#8A94A6]">{rel(last?.created_at)}</span>
                      </div>
                      <p className="text-xs text-[#525866] truncate">
                        {isInbound ? '' : '↳ '}{last?.body || '(kein Text)'}
                      </p>
                    </div>
                    {unread && (
                      <span className="bg-[#25D366] text-white text-[10px] font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center">
                        {t.unread_count}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <WhatsAppChatDrawer
        open={!!active}
        onOpenChange={(o) => !o && (setActive(null), load())}
        customer={active}
        onSent={load}
      />
    </div>
  );
};

export default Messages;
